<template>
  <div class="editable-table-container">
    <el-table
      ref="tableRef"
      v-bind="$attrs"
      v-on="$listeners"
      :data="currentPageData"
      :border="border"
      :row-key="rowIdKey"
      :select-on-indeterminate="false"
      style="width: 100%"
      header-cell-class-name="custom-table-header"
      :row-style="getRowStyle"
      :tree-props="treeProps"
      :default-expand-all="defaultExpandAll"
      :expand-row-keys="defaultExpandAll ? undefined : localExpandRowKeys"
      :height="resolvedHeight"
      @selection-change="handleSelectionChange"
      @sort-change="handleSortChange"
      @filter-change="handleFilterChange"
      @expand-change="handleExpandChange"
    >
      <render-table-column
        v-for="(col, index) in getRenderColumns"
        :key="col.prop || col.label || `col-${index}`"
        :column="col"
        :column-index="index"
        :global-editable="editable"
        :editing-cells="editingCells"
        :row-id-key="rowIdKey"
        :current-editing-old-value="currentEditingOldValue"
        :is-editing="!!Object.keys(editingCells).length"
        :is-tree-table="isTreeTable"
        :tree-children-key="treeChildrenKey"
        :tree-indent="treeIndent"
        :local-expand-row-keys="localExpandRowKeys"
        :table-data="currentPageData"
        @cell-click="handleCellClick"
        @cell-edit-start="handleCellEditStart"
        @cell-edit-confirm="handleCellEditConfirm"
        @cell-edit-cancel="handleCellEditCancel"
        @operate-btn-click="handleOperateBtnClick"
        @edit-cancel-confirm="handleEditCancelConfirm"
      />
    </el-table>
    <div v-if="useInternalPagination" class="table-pagination">
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="pageSizes"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handlePageSizeChange"
        @current-change="handleCurrentPageChange"
      />
    </div>
  </div>
</template>

<script>
const RenderTableColumn = {
  name: 'RenderTableColumn',
  props: {
    column: { type: Object, required: true },
    columnIndex: { type: Number, default: 0 },
    globalEditable: { type: Boolean, default: false },
    editingCells: { type: Object, default: () => ({}) },
    rowIdKey: { type: String, default: 'id' },
    currentEditingOldValue: { type: [String, Number, Boolean, null], default: null },
    isEditing: { type: Boolean, default: false },
    isTreeTable: { type: Boolean, default: false },
    treeChildrenKey: { type: String, default: 'children' },
    treeIndent: { type: Number, default: 16 },
    localExpandRowKeys: { type: Array, default: () => [] },
    tableData: { type: Array, default: () => [] }
  },
  methods: {
    getRowKey (row) {
      return row[this.rowIdKey] || JSON.stringify(row)
    },
    isCellEditing (row, prop) {
      const rowKey = this.getRowKey(row)
      return this.editingCells[rowKey] && this.editingCells[rowKey][prop] === true
    },
    isColumnEditable () {
      if (this.column.notEditable) return false
      return this.column.editable !== undefined ? this.column.editable : this.globalEditable
    },
    triggerCellClick (row, prop) {
      if (!this.isColumnEditable()) return
      this.$emit('cell-click', row, prop, row[prop])
    },
    triggerEditEvent (type, row, prop, value, oldValue) {
      if (!this.isColumnEditable()) return
      this.$emit(`cell-edit-${type}`, { row, prop, value, oldValue })
    },
    async triggerEditCancelConfirm (row, prop, currentValue, oldValue) {
      if (!this.isColumnEditable()) return false
      const resolve = await new Promise(resolve => {
        this.$emit('edit-cancel-confirm', { row, prop, currentValue, oldValue }, resolve)
      })
      if (resolve) {
        this.triggerEditEvent('cancel', row, prop)
      }
      return resolve
    },
    getOperationClass (btn) {
      const text = String(btn.text || '')
      const type = String(btn.type || '').trim()
      const classList = ['aj-table-operation-link']
      const knownTypes = ['primary', 'success', 'warning', 'danger', 'info']
      const actionMap = [
        { key: 'danger', words: ['删除', '驳回', '移除', '作废', '失败', '提交删除'] },
        { key: 'approve', words: ['通过', '确认', '确定', '处理', '已读'] },
        { key: 'submit', words: ['提交', '保存', '生成', '整合'] },
        { key: 'edit', words: ['编辑', '修改', '重置密码', '审核'] },
        { key: 'reset', words: ['重置', '刷新', '过期'] },
        { key: 'export', words: ['导出', '下载', '保存到电脑'] },
        { key: 'view', words: ['查看', '详情', '预览', '时间段', '分配权限'] },
        { key: 'primary', words: ['新增', '添加'] }
      ]
      const matched = actionMap.find(item => item.words.some(word => text.includes(word)))
      const resolvedType = matched ? matched.key : (knownTypes.includes(type) ? type : 'default')
      classList.push(`aj-table-operation-link--${resolvedType}`)
      if (btn.class) classList.push(btn.class)
      return classList
    },
    getStatusClass (column, row, content) {
      const prop = String(column.prop || '').toLowerCase()
      const label = String(column.label || '')
      if (!prop.includes('status') && !label.includes('状态')) return ''
      const rawValue = row[column.prop]
      const text = String(content && content.text ? content.text : content || '')
      const classList = ['aj-table-status-pill']
      if ([2, '2'].includes(rawValue) || ['审核中', '生成中', '处理中', '待处理', '待审核', '已提交', '未读'].some(word => text.includes(word))) {
        classList.push('aj-table-status-pill--warning')
      } else if ([3, '3'].includes(rawValue) || ['通过', '完成', '成功', '已处理', '已发包', '已归档', '已读'].some(word => text.includes(word))) {
        classList.push('aj-table-status-pill--success')
      } else if (['失败', '驳回', '删除', '过期', '未处理'].some(word => text.includes(word))) {
        classList.push('aj-table-status-pill--danger')
      } else {
        classList.push('aj-table-status-pill--primary')
      }
      return classList
    },
    getTreeLevel (row) {
      if (row.level !== undefined) return row.level
      let level = 0
      let currentParentId = row.parentId
      while (currentParentId) {
        level += 1
        const parentRow = this.tableData.find(item => item[this.rowIdKey] === currentParentId)
        if (!parentRow) break
        currentParentId = parentRow.parentId
      }
      return level
    },
    hasChildren (row) {
      const children = row[this.treeChildrenKey]
      return this.isTreeTable && Array.isArray(children) && children.length > 0
    },
    isRowExpanded (row) {
      return this.localExpandRowKeys.includes(row[this.rowIdKey])
    },
    getFirstContentColumnIndex () {
      const contentColumns = (this.$parent.columns || []).filter(col => !['selection', 'index', 'operation'].includes(col.type))
      return contentColumns.findIndex(col => col.prop === this.column.prop)
    }
  },
  render (h) {
    const column = this.column
    const canEdit = this.isColumnEditable()
    const align = column.align || 'center'
    const alignStyle = {
      textAlign: align,
      justifyContent: align === 'center' ? 'center' : 'flex-start'
    }
    const columnProps = {
      label: column.label,
      prop: column.prop,
      width: column.width,
      minWidth: column.minWidth,
      fixed: column.fixed,
      resizable: column.resizable !== undefined ? column.resizable : true,
      sortable: column.sortable !== undefined ? column.sortable : false,
      sortMethod: column.sortMethod,
      sortBy: column.sortBy,
      sortOrders: column.sortOrders || ['ascending', 'descending', null],
      type: column.type,
      index: column.index,
      formatter: column.formatter,
      showOverflowTooltip: column.showOverflowTooltip || false
    }

    if (Array.isArray(column.filters) && column.filters.length > 0) {
      Object.assign(columnProps, {
        filters: column.filters,
        filterMethod: column.filterMethod,
        filterMultiple: column.filterMultiple !== undefined ? column.filterMultiple : true,
        filteredValue: column.filteredValue
      })
    }

    if (this.isEditing) {
      columnProps.sortable = false
      delete columnProps.filters
    }

    if (column.type === 'selection') {
      return h('el-table-column', { props: { ...columnProps, type: 'selection' }, attrs: { align: 'center' } })
    }

    if (column.type === 'index') {
      return h('el-table-column', {
        props: { ...columnProps, type: 'index', label: column.label || '序号' },
        attrs: { align },
        scopedSlots: {
          default: scope => {
            const slot = this.$parent.$scopedSlots[`cell-${column.prop || 'index'}`]
            return h('div', { class: 'aj-table-cell', style: { justifyContent: alignStyle.justifyContent } }, [
              slot ? slot({ row: scope.row, index: scope.$index }) : h('span', scope.$index + 1)
            ])
          }
        }
      })
    }

    if (column.children && column.children.length) {
      return h('el-table-column', { props: { ...columnProps }, attrs: { align } }, column.children.map((childCol, idx) => h('render-table-column', {
        key: childCol.prop || `child-col-${idx}`,
        props: {
          column: childCol,
          columnIndex: idx,
          globalEditable: this.globalEditable,
          editingCells: this.editingCells,
          rowIdKey: this.rowIdKey,
          currentEditingOldValue: this.currentEditingOldValue,
          isEditing: this.isEditing,
          isTreeTable: this.isTreeTable,
          treeChildrenKey: this.treeChildrenKey,
          treeIndent: this.treeIndent,
          localExpandRowKeys: this.localExpandRowKeys,
          tableData: this.tableData
        },
        on: {
          'cell-click': this.triggerCellClick,
          'cell-edit-start': data => this.triggerEditEvent('start', data.row, data.prop, data.value, data.oldValue),
          'cell-edit-confirm': data => this.triggerEditEvent('confirm', data.row, data.prop, data.value, data.oldValue),
          'cell-edit-cancel': data => this.triggerEditEvent('cancel', data.row, data.prop),
          'operate-btn-click': data => this.$emit('operate-btn-click', data),
          'edit-cancel-confirm': this.triggerEditCancelConfirm
        }
      })))
    }

    if (column.type === 'operation') {
      return h('el-table-column', {
        props: { ...columnProps },
        attrs: { align },
        scopedSlots: {
          default: scope => {
            const operationSlot = this.$parent.$scopedSlots[`cell-${column.prop || 'operation'}`]
            if (operationSlot) return operationSlot({ row: scope.row, index: scope.$index })
            const btns = typeof column.buttons === 'function' ? column.buttons(scope.row) : (column.buttons || [])
            return h('div', { class: 'aj-table-cell', style: { justifyContent: alignStyle.justifyContent } }, btns.map((btn, btnIdx) => {
              const hidden = typeof btn.isShow === 'function' ? btn.isShow(scope.row) : btn.isShow
              if (hidden) return null
              return h('el-link', {
                key: btnIdx,
                props: {
                  type: btn.type || 'primary',
                  icon: btn.icon,
                  disabled: typeof btn.disabled === 'function' ? btn.disabled(scope.row) : btn.disabled || false
                },
                class: this.getOperationClass(btn),
                on: {
                  click: e => {
                    e.preventDefault()
                    e.stopPropagation()
                    this.$emit('operate-btn-click', { btn, row: scope.row })
                  }
                }
              }, [btn.text])
            }))
          }
        }
      })
    }

    return h('el-table-column', {
      props: { ...columnProps },
      attrs: { align },
      scopedSlots: {
        header: scope => {
          const headerSlot = this.$parent.$scopedSlots[`header-${column.prop || column.label}`]
          return h('div', { class: 'aj-table-header-cell', style: { justifyContent: alignStyle.justifyContent } }, [
            headerSlot ? headerSlot({ column: scope.column, $index: scope.$index }) : h('span', column.label)
          ])
        },
        default: scope => {
          const row = scope.row
          const prop = column.prop
          const isEditingCell = this.isCellEditing(row, prop)
          const currentValue = row[prop]
          const oldValue = this.currentEditingOldValue
          const treePrefix = null

          if (isEditingCell) {
            const editSlot = this.$parent.$scopedSlots[`edit-cell-${prop}`]
            if (editSlot) return editSlot({ row, prop, value: currentValue, oldValue })
            return h('div', { class: 'edit-cell-content', on: { click: e => e.stopPropagation() } }, [
              h('div', { class: 'aj-table-edit-row' }, [
                treePrefix,
                h('el-input', {
                  props: { value: currentValue, size: 'mini', placeholder: `请输入${column.label}` },
                  attrs: { 'data-edit-cell': `${this.getRowKey(row)}-${prop}` },
                  on: {
                    input: val => this.$set(row, prop, val),
                    'keyup.enter': () => this.triggerEditEvent('confirm', row, prop, row[prop], oldValue)
                  }
                })
              ]),
              h('div', { class: 'aj-table-edit-actions' }, [
                h('el-button', { props: { size: 'mini', type: 'text' }, on: { click: e => { e.stopPropagation(); this.triggerEditEvent('confirm', row, prop, row[prop], oldValue) } } }, ['确认']),
                h('el-button', { props: { size: 'mini', type: 'text' }, style: { color: '#f56c6c' }, on: { click: async e => { e.stopPropagation(); await this.triggerEditCancelConfirm(row, prop, currentValue, oldValue) } } }, ['取消'])
              ])
            ])
          }

          const slot = this.$parent.$scopedSlots[`cell-${prop}`]
          const displayValue = row[prop] === undefined || row[prop] === null ? '' : String(row[prop])
          let content = displayValue
          if (slot) {
            content = slot({ row, column, index: scope.$index })
          } else if (typeof column.render === 'function') {
            content = column.render(h, { row, column, index: scope.$index })
          }
          const tooltipContent = row[column.tooltipField || prop] == null ? '' : String(row[column.tooltipField || prop])
          const statusClass = this.getStatusClass(column, row, content)
          const normalizedContent = statusClass ? h('span', { class: statusClass }, [content]) : content
          const cellContent = h('div', {
            class: 'aj-table-cell-text',
            style: { WebkitLineClamp: column.lines || 3, textAlign: align }
          }, [treePrefix, normalizedContent])

          const container = h('div', {
            class: 'aj-table-cell-content',
            style: {
              cursor: canEdit ? 'pointer' : 'text',
              padding: `${column.cellPadding !== undefined ? column.cellPadding : this.$parent.cellPadding}px`,
              textAlign: align
            },
            on: canEdit ? { click: e => { e.stopPropagation(); this.triggerCellClick(row, prop) } } : {}
          }, [cellContent])

          if (column.showTooltip === true && tooltipContent) {
            return h('el-tooltip', {
              props: {
                effect: column.tooltipEffect || 'dark',
                placement: column.tooltipPlacement || 'top',
                content: tooltipContent,
                openDelay: column.tooltipOpenDelay !== undefined ? column.tooltipOpenDelay : 100,
                popperClass: 'aj-table-tooltip-popper'
              }
            }, [container])
          }
          return container
        }
      }
    })
  }
}

export default {
  name: 'AjTable',
  components: { RenderTableColumn },
  props: {
    data: { type: Array, required: true, default: () => [] },
    columns: { type: Array, required: true, default: () => [] },
    editable: { type: Boolean, default: false },
    useInternalPagination: { type: Boolean, default: false },
    currentPage: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 },
    pageSizes: { type: Array, default: () => [10, 20, 30, 50, 100] },
    total: { type: Number, default: 0 },
    rowIdKey: { type: String, default: 'id' },
    border: { type: Boolean, default: true },
    tableSize: { type: String, default: 'default' },
    rowHeight: { type: Number, default: 44 },
    cellPadding: { type: Number, default: 9 },
    visibleColumns: { type: Object, default: () => ({}) },
    cancelEditTip: { type: String, default: '当前单元格内容已修改，是否确认放弃修改？' },
    isTreeTable: { type: Boolean, default: false },
    treeChildrenKey: { type: String, default: 'children' },
    defaultExpandAll: { type: Boolean, default: false },
    expandRowKeys: { type: Array, default: () => [] },
    treeIndent: { type: Number, default: 16 },
    autoHeight: { type: Boolean, default: false },
    autoHeightOffset: { type: Number, default: 0 }
  },
  data () {
    return {
      editingCells: Object.create(null),
      currentEditingOldValue: null,
      currentEditingCell: { rowKey: null, prop: null },
      localCheckedIds: [],
      isShowingCancelConfirm: false,
      eventLock: false,
      localExpandRowKeys: [...this.expandRowKeys],
      isPaging: false,
      lastRestoreSelectionIds: null,
      computedHeight: null
    }
  },
  computed: {
    currentPageData () {
      return Array.isArray(this.data) ? this.data : []
    },
    getRenderColumns () {
      if (!Array.isArray(this.columns) || this.columns.length === 0) return []
      this.initVisibleColumns()
      const filterCols = cols => cols.map(col => {
        if (!col) return null
        if (col.children && Array.isArray(col.children)) {
          const filteredChildren = filterCols(col.children).filter(Boolean)
          return { ...col, children: filteredChildren }
        }
        const key = col.prop || col.label
        return this.visibleColumns[key] === false ? null : col
      }).filter(Boolean)
      return filterCols([...this.columns])
    },
    localVisibleColumns () {
      return { ...this.visibleColumns }
    },
    treeProps () {
      return { children: this.treeChildrenKey, indent: this.treeIndent }
    },
    resolvedHeight () {
      if (this.$attrs.height !== undefined) return this.$attrs.height
      if (this.autoHeight) return this.computedHeight
      return undefined
    }
  },
  watch: {
    expandRowKeys: {
      immediate: true,
      handler (newVal) {
        this.localExpandRowKeys = [...newVal]
      }
    }
  },
  methods: {
    updateHeight () {
      if (!this.autoHeight) return
      this.$nextTick(() => {
        const el = this.$el
        if (!el) return
        const rect = el.getBoundingClientRect()
        const paginationHeight = this.useInternalPagination ? 56 : 0
        const height = window.innerHeight - rect.top - this.autoHeightOffset - paginationHeight
        this.computedHeight = Math.max(height, 200)
      })
    },
    getRowStyle () {
      const heightMap = {
        compact: 40,
        default: this.rowHeight,
        comfortable: Math.max(this.rowHeight, 48)
      }
      return {
        height: `${heightMap[this.tableSize] || this.rowHeight}px`
      }
    },
    initVisibleColumns () {
      const init = cols => {
        if (!Array.isArray(cols)) return
        cols.forEach(col => {
          const key = col && (col.prop || col.label)
          if (!key) return
          if (this.visibleColumns[key] === undefined) this.$set(this.visibleColumns, key, true)
          if (col.children) init(col.children)
        })
      }
      init(this.columns)
      this.$emit('update:visibleColumns', this.localVisibleColumns)
    },
    handleSelectionChange (selRows) {
      if (!Array.isArray(selRows) || this.isPaging) return
      const currentIds = this.currentPageData.map(row => row[this.rowIdKey])
      const currentSelectIds = selRows.map(row => row[this.rowIdKey]).sort()
      if (this.lastRestoreSelectionIds && JSON.stringify(this.lastRestoreSelectionIds) === JSON.stringify(currentSelectIds)) return
      this.localCheckedIds = this.localCheckedIds.filter(id => !currentIds.includes(id))
      this.localCheckedIds = [...new Set([...this.localCheckedIds, ...selRows.map(row => row[this.rowIdKey])])]
      this.$emit('update:checkedIds', this.localCheckedIds)
      this.$emit('selection-change', selRows)
    },
    handlePageSizeChange (val) {
      this.isPaging = true
      this.lastRestoreSelectionIds = null
      this.$emit('update:pageSize', val)
      this.$emit('page-change', { currentPage: this.currentPage, pageSize: val })
      setTimeout(() => { this.restoreSelection(); this.isPaging = false }, 50)
    },
    handleCurrentPageChange (val) {
      this.isPaging = true
      this.lastRestoreSelectionIds = null
      this.$emit('update:currentPage', val)
      this.$emit('page-change', { currentPage: val, pageSize: this.pageSize })
      setTimeout(() => { this.restoreSelection(); this.isPaging = false }, 50)
    },
    handleSortChange (sort) {
      this.$emit('sort-change', sort)
    },
    handleFilterChange (filters) {
      this.$emit('filter-change', filters)
    },
    handleCellClick (row, prop, oldValue) {
      const targetColumn = this.getRenderColumns.find(col => col.prop === prop)
      const columnCanEdit = targetColumn && targetColumn.editable !== undefined ? targetColumn.editable : this.editable
      if (!columnCanEdit || this.eventLock || this.isShowingCancelConfirm) return
      if (this.currentEditingCell.rowKey) {
        this.handleCancelEditing(true)
        return
      }
      const rowKey = row[this.rowIdKey] || JSON.stringify(row)
      this.clearEditingState()
      this.$set(this.editingCells, rowKey, { [prop]: true })
      this.currentEditingCell = { rowKey, prop }
      this.currentEditingOldValue = oldValue
      this.$emit('cell-edit-start', { row, prop, oldValue })
    },
    handleCellEditStart (data) {
      this.$emit('cell-edit-start', data)
    },
    handleCellEditConfirm (data) {
      if (this.eventLock) return
      this.eventLock = true
      const { row, prop, value, oldValue } = data
      this.forceCloseEditState()
      if (value !== oldValue) {
        this.$set(row, prop, value)
        this.$emit('cell-edit-confirm', data)
      }
      this.$nextTick(() => { this.eventLock = false })
    },
    handleCellEditCancel (data) {
      if (this.eventLock) return
      this.handleCancelEditing(false)
      this.$emit('cell-edit-cancel', data)
      this.$nextTick(() => { this.eventLock = false })
    },
    handleEditCancelConfirm ({ row, prop, currentValue, oldValue }, resolve) {
      if (this.isShowingCancelConfirm || this.eventLock) return resolve(false)
      this.isShowingCancelConfirm = true
      this.eventLock = true
      this.$confirm(this.cancelEditTip, '提示', { confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning' })
        .then(() => { this.$set(row, prop, oldValue); this.clearEditingState(); resolve(true) })
        .catch(() => resolve(false))
        .finally(() => { this.isShowingCancelConfirm = false; this.eventLock = false })
    },
    handleCancelEditing () {
      if (!this.currentEditingCell.rowKey || !this.currentEditingCell.prop || this.eventLock || this.isShowingCancelConfirm) return
      const { rowKey, prop } = this.currentEditingCell
      const currentRow = this.currentPageData.find(row => (row[this.rowIdKey] || JSON.stringify(row)) === rowKey)
      if (!currentRow) {
        this.clearEditingState()
        return
      }
      if (currentRow[prop] === this.currentEditingOldValue) {
        this.clearEditingState()
        return
      }
      this.eventLock = true
      this.$confirm(this.cancelEditTip, '提示', { confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning' })
        .then(() => { this.$set(currentRow, prop, this.currentEditingOldValue); this.clearEditingState(); this.eventLock = false })
        .catch(() => { this.eventLock = false })
    },
    clearEditingState () {
      this.editingCells = Object.create(null)
      this.currentEditingCell = { rowKey: null, prop: null }
      this.currentEditingOldValue = null
      this.isShowingCancelConfirm = false
      this.eventLock = false
      this.$nextTick(() => { if (this.$refs.tableRef) this.$refs.tableRef.doLayout() })
    },
    forceCloseEditState () {
      const { rowKey, prop } = this.currentEditingCell
      if (rowKey && prop && this.editingCells[rowKey]) {
        this.$delete(this.editingCells[rowKey], prop)
        if (Object.keys(this.editingCells[rowKey]).length === 0) this.$delete(this.editingCells, rowKey)
      }
      this.currentEditingCell = { rowKey: null, prop: null }
      this.currentEditingOldValue = null
      this.isShowingCancelConfirm = false
      this.eventLock = false
      this.$nextTick(() => { if (this.$refs.tableRef) this.$refs.tableRef.doLayout() })
    },
    handleOperateBtnClick (data) {
      this.$emit('operate-btn-click', data)
    },
    handleGlobalClick (e) {
      if (e.ctrlKey || e.metaKey || !Object.keys(this.editingCells).length) return
      const selection = window.getSelection()
      if (selection && selection.toString().length > 0) return
      const isEditArea = e.target.closest('.edit-cell-content') || e.target.closest('[data-edit-cell]')
      const isDialogArea = e.target.closest('.el-message-box') || e.target.closest('.el-dialog')
      if (isEditArea || isDialogArea || this.eventLock || this.isShowingCancelConfirm) return
      if (!e.target.closest('.editable-table-container')) this.handleCancelEditing()
    },
    handleExpandChange (row, expanded) {
      const rowKey = row[this.rowIdKey]
      if (expanded && !this.localExpandRowKeys.includes(rowKey)) this.localExpandRowKeys.push(rowKey)
      if (!expanded) this.localExpandRowKeys = this.localExpandRowKeys.filter(id => id !== rowKey)
      row.expanded = expanded
      this.$emit('update:expandRowKeys', [...this.localExpandRowKeys])
      this.$emit('expand-change', row, expanded)
    },
    expandAll () {
      if (!this.isTreeTable || !this.currentPageData.length) return
      const getAllRowIds = data => data.reduce((ids, row) => {
        row.expanded = true
        const children = row[this.treeChildrenKey]
        return ids.concat(row[this.rowIdKey], Array.isArray(children) ? getAllRowIds(children) : [])
      }, [])
      this.localExpandRowKeys = getAllRowIds(this.currentPageData)
      this.$emit('update:expandRowKeys', [...this.localExpandRowKeys])
    },
    collapseAll () {
      if (!this.isTreeTable) return
      const collapseRows = data => data.forEach(row => {
        row.expanded = false
        const children = row[this.treeChildrenKey]
        if (Array.isArray(children)) collapseRows(children)
      })
      collapseRows(this.currentPageData)
      this.localExpandRowKeys = []
      this.$emit('update:expandRowKeys', [])
    },
    clearSelection () {
      if (this.$refs.tableRef) this.$refs.tableRef.clearSelection()
      this.localCheckedIds = []
      this.$emit('update:checkedIds', [])
    },
    restoreSelection () {
      if (!this.$refs.tableRef) return
      const idsToSelect = this.currentPageData.map(row => row[this.rowIdKey]).filter(id => this.localCheckedIds.includes(id))
      if (!idsToSelect.length) return
      const rowsToSelect = this.currentPageData.filter(row => idsToSelect.includes(row[this.rowIdKey]))
      this.$refs.tableRef.clearSelection()
      rowsToSelect.forEach(row => this.$refs.tableRef.toggleRowSelection(row, true))
      this.lastRestoreSelectionIds = idsToSelect.sort()
    }
  },
  mounted () {
    document.addEventListener('click', this.handleGlobalClick, { capture: true, passive: true })
    this.$nextTick(() => {
      if (this.$refs.tableRef) this.$refs.tableRef.doLayout()
      if (this.autoHeight) this.updateHeight()
    })
    if (this.autoHeight) {
      window.addEventListener('resize', this.updateHeight)
    }
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handleGlobalClick, { capture: true })
    if (this.autoHeight) {
      window.removeEventListener('resize', this.updateHeight)
    }
    this.clearEditingState()
  }
}
</script>

<style scoped>
.editable-table-container {
  width: 100%;
}

.table-pagination {
  margin-top: 16px;
  text-align: right;
}

.aj-table-cell,
.aj-table-header-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.aj-table-cell-content {
  width: 100%;
  min-height: 100%;
  box-sizing: border-box;
}

.aj-table-cell-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  width: 100%;
  line-height: 1.5;
  color: #2f3a4a;
}

.aj-table-operation-link {
  margin: 0 2px;
  font-size: 12px;
  font-weight: 500;
}

.aj-table-edit-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.aj-table-edit-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
}

::v-deep .el-table {
  color: #2f3a4a;
  font-size: 13px;
}

::v-deep .el-table::before {
  background-color: #e5eaf3 !important;
}

::v-deep .custom-table-header {
  background-color: #f7f9fc !important;
  color: #5b667a !important;
  font-weight: 600 !important;
  padding-left: 0 !important;
}

::v-deep .el-table th {
  background-color: #f7f9fc !important;
}

::v-deep .el-table td,
::v-deep .el-table th {
  padding: 0 !important;
  border-color: #e8edf5 !important;
}

::v-deep .el-table--border,
::v-deep .el-table--border th,
::v-deep .el-table--border td {
  border-color: #e8edf5 !important;
}

::v-deep .el-table td .cell,
::v-deep .el-table th .cell {
  width: 100% !important;
  padding: 0 !important;
  overflow: hidden;
}

::v-deep .el-table td .cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
}

::v-deep .el-table th .cell {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5b667a;
}

::v-deep .el-table__row {
  transition: background-color 0.18s ease;
}

::v-deep .el-table__row td {
  background: #fff;
}

::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background: #fafbfd;
}

::v-deep .el-table--enable-row-hover .el-table__body tr:hover > td {
  background: #f3f7ff !important;
}

::v-deep .el-table__fixed,
::v-deep .el-table__fixed-right {
  height: 100% !important;
  background: #fff;
  box-shadow: none;
}

::v-deep .el-table__fixed::before,
::v-deep .el-table__fixed-right::before {
  background-color: #e8edf5 !important;
}

::v-deep .el-table__fixed-body-wrapper .cell,
::v-deep .el-table__fixed-right .cell,
::v-deep .el-table__fixed .cell {
  background: inherit;
  position: relative;
  z-index: 2;
}

::v-deep .el-table__fixed-body-wrapper .el-table__row td,
::v-deep .el-table__fixed-right .el-table__row td,
::v-deep .el-table__fixed .el-table__row td {
  background: #fff;
}

::v-deep .el-table__fixed-body-wrapper .el-table__row--striped td,
::v-deep .el-table__fixed-right .el-table__row--striped td,
::v-deep .el-table__fixed .el-table__row--striped td {
  background: #fafbfd;
}

::v-deep .el-table__fixed-body-wrapper .el-table__row:hover td,
::v-deep .el-table__fixed-right .el-table__row:hover td,
::v-deep .el-table__fixed .el-table__row:hover td {
  background: #f3f7ff !important;
}

::v-deep .el-table__body-wrapper {
  position: relative;
  z-index: 1;
}

::v-deep .el-table .caret-wrapper {
  height: 16px;
}

::v-deep .el-table .sort-caret.ascending {
  top: -4px;
}

::v-deep .el-table .sort-caret.descending {
  bottom: -2px;
}

::v-deep .el-tooltip {
  width: 100%;
  display: block;
}

::v-deep .el-pagination {
  padding: 0;
  color: #5b667a;
  font-weight: 400;
}

::v-deep .el-pagination button,
::v-deep .el-pagination .btn-next,
::v-deep .el-pagination .btn-prev,
::v-deep .el-pagination .el-pager li {
  min-width: 30px;
  height: 30px;
  line-height: 30px;
  border-radius: 6px;
}

::v-deep .el-pagination .el-pager li:not(.disabled).active {
  background: #2f6bff;
  color: #fff;
}

::v-deep .el-link--inner {
  white-space: nowrap;
}

::v-deep .el-icon-arrow-right.el-table__expand-icon--expanded {
  transform: rotate(90deg);
}
</style>

<style>
.aj-table-tooltip-popper {
  max-width: 240px !important;
  word-break: break-all;
  line-height: 1.5;
  font-size: 12px;
}
</style>
